/**
 * ReorderModal — Edit quantities and re-add order items to cart
 *
 * Shows all line items from the original order with editable qty fields.
 * User can remove individual lines, then click "Reorder" to add to cart.
 *
 * Uses: CartContext IMPORT_ITEMS action
 *
 * @module components/commerce/ReorderModal
 * @since 2026-05-25 (Group G)
 */
import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Modal from '@components/common/Modal';
import Toast from '@components/common/Toast';
import { useCart } from '@contexts/CartContext';

export default function ReorderModal({ order, onClose }) {
  const { dispatch } = useCart();
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);

  // Initialise editable lines from the order
  const [lines, setLines] = useState(() =>
    (order?.lines || []).map(line => ({
      itemCode: line.itemCode,
      description: line.description,
      unitPrice: line.unitPrice,
      weightKg: line.weightKg,
      weightLb: line.weightLb,
      bundledQty: line.bundledQty || 0,
      shippingCost: line.shippingCost || 0,
      productLine: line.productLine,
      category: line.category,
      availability: line.availability || 'In stock',
      estimatedDeliveryDays: line.estimatedDeliveryDays,
      qty: line.qty,
    }))
  );

  const removeLine = useCallback((itemCode) => {
    setLines(prev => prev.filter(l => l.itemCode !== itemCode));
  }, []);

  const updateQty = useCallback((itemCode, qty) => {
    const val = Math.max(1, parseInt(qty) || 1);
    setLines(prev => prev.map(l => l.itemCode === itemCode ? { ...l, qty: val } : l));
  }, []);

  const handleReorder = useCallback(() => {
    if (lines.length === 0) return;

    const cartItems = lines.map(l => ({
      productId: l.itemCode,
      itemCode: l.itemCode,
      description: l.description,
      unitPrice: l.unitPrice,
      weightKg: l.weightKg,
      weightLb: l.weightLb,
      bundledQty: l.bundledQty,
      shippingCost: l.shippingCost,
      productLine: l.productLine,
      category: l.category,
      availability: l.availability,
      estimatedDeliveryDays: l.estimatedDeliveryDays,
      quantity: l.qty,
    }));

    dispatch({ type: 'IMPORT_ITEMS', payload: cartItems });
    setToast({ type: 'success', message: `${lines.length} item${lines.length > 1 ? 's' : ''} added to cart` });
    setTimeout(() => {
      onClose();
      navigate('/cart');
    }, 1500);
  }, [lines, dispatch, onClose, navigate]);

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
        title="Reorder products"
        size="lg"
      >
        <div className="px-1">
          <p className="text-sm text-n5 mb-4">
            Product summary — adjust quantities or remove items before adding to cart.
          </p>

          {lines.length === 0 ? (
            <div className="text-center py-8">
              <span className="material-symbols-outlined text-3xl text-n3 mb-2 block">remove_shopping_cart</span>
              <p className="text-sm text-n5">No items to reorder.</p>
            </div>
          ) : (
            <div className="border border-n2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-n2 bg-n1 text-xs text-n5">
                    <th className="text-left px-4 py-2.5 font-normal w-14">Image</th>
                    <th className="text-left px-4 py-2.5 font-normal">Item details</th>
                    <th className="text-center px-4 py-2.5 font-normal w-32">Re-order quantity</th>
                    <th className="text-center px-4 py-2.5 font-normal w-24">Remove</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map(line => (
                    <tr key={line.itemCode} className="border-b border-n1">
                      <td className="px-4 py-3">
                        <div className="w-10 h-10 bg-n1 border border-n2 flex items-center justify-center">
                          <span className="material-symbols-outlined text-n3 text-sm">
                            {line.productLine === 'dth' ? 'hardware' : 'construction'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-carbon-black font-normal">{line.description}</p>
                        <p className="text-xs text-n5 mt-0.5">{line.itemCode}</p>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <input
                          type="number"
                          min="1"
                          value={line.qty}
                          onChange={e => updateQty(line.itemCode, e.target.value)}
                          className="w-20 border border-n3 px-2 py-1.5 text-center text-sm focus:ring-1 focus:ring-electric-blue focus:border-electric-blue"
                        />
                      </td>
                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => removeLine(line.itemCode)}
                          className="text-xs text-red-600 hover:text-red-800 flex items-center gap-1 mx-auto"
                        >
                          <span className="material-symbols-outlined text-sm">close</span>
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Footer buttons */}
        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-n2">
          <button
            onClick={onClose}
            className="px-5 py-2.5 border border-n3 text-sm text-carbon-black hover:border-carbon-black transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleReorder}
            disabled={lines.length === 0}
            className={`px-5 py-2.5 text-sm text-white transition-colors flex items-center gap-2
              ${lines.length > 0
                ? 'bg-electric-blue hover:bg-eb-3'
                : 'bg-n3 cursor-not-allowed'}`}
          >
            <span className="material-symbols-outlined text-base">shopping_cart</span>
            Reorder
          </button>
        </div>
      </Modal>

      {toast && <Toast type={toast.type} message={toast.message} onClose={() => setToast(null)} />}
    </>
  );
}
